import { ApiManager } from './ApiManager';

export type PatientDocument = {
  id: string;
  // null when this document comes from a list response — the backend only
  // signs a file URL (a paid IAM API call) for single-document responses,
  // i.e. after calling retrieve()/retrieveForCaregiver().
  file_url: string | null;
  filename: string;
  uploaded_at: string;
  shared_with_caregivers: string[];
};

export type ApiPatientDocumentsListParams = {
  patientId?: string;
};

export type ApiPatientDocumentsListResult = PatientDocument[];

export type ApiPatientDocumentsUploadParams = {
  file: File;
  caregiverId?: string;
};

export type ApiPatientDocumentsUploadResult = PatientDocument;

export type ApiPatientDocumentsShareParams = {
  documentId: string;
  caregiverId: string;
};

export type ApiPatientDocumentsShareResult = PatientDocument;

export type ApiPatientDocumentsUnshareParams = {
  documentId: string;
  caregiverId: string;
};

export type ApiPatientDocumentsUnshareResult = PatientDocument;

export type ApiPatientDocumentsUpdateParams = {
  documentId: string;
  filename: string;
};

export type ApiPatientDocumentsUpdateResult = PatientDocument;

export default class ApiPatientDocumentsManager {
  static async list(params: ApiPatientDocumentsListParams = {}): Promise<ApiPatientDocumentsListResult> {
    const apiManager = ApiManager.getInstance();
    const queryParams = new URLSearchParams();
    if (params.patientId) {
      queryParams.append('patient_id', params.patientId);
    }
    const url = queryParams.toString()
      ? `/api/patient-documents/?${queryParams.toString()}`
      : '/api/patient-documents/';
    const response = await apiManager.get(url);
    return response.data as ApiPatientDocumentsListResult;
  }

  static async listForCaregiver(params: ApiPatientDocumentsListParams = {}): Promise<ApiPatientDocumentsListResult> {
    const apiManager = ApiManager.getInstance();
    const queryParams = new URLSearchParams();
    if (params.patientId) {
      queryParams.append('patient_id', params.patientId);
    }
    const url = queryParams.toString()
      ? `/api/documents-for-caregivers/?${queryParams.toString()}`
      : '/api/documents-for-caregivers/';
    const response = await apiManager.get(url);
    return response.data as ApiPatientDocumentsListResult;
  }

  static async retrieve(documentId: string): Promise<PatientDocument> {
    const apiManager = ApiManager.getInstance();
    const response = await apiManager.get(`/api/patient-documents/${documentId}/`);
    return response.data as PatientDocument;
  }

  static async retrieveForCaregiver(documentId: string): Promise<PatientDocument> {
    const apiManager = ApiManager.getInstance();
    const response = await apiManager.get(`/api/documents-for-caregivers/${documentId}/`);
    return response.data as PatientDocument;
  }

  static async upload(params: ApiPatientDocumentsUploadParams): Promise<ApiPatientDocumentsUploadResult> {
    const apiManager = ApiManager.getInstance();
    const formData = new FormData();
    formData.append('file', params.file);
    if (params.caregiverId) {
      formData.append('caregiver_id', params.caregiverId);
    }

    const response = await apiManager.post('/api/patient-documents/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data as ApiPatientDocumentsUploadResult;
  }

  static async share(params: ApiPatientDocumentsShareParams): Promise<ApiPatientDocumentsShareResult> {
    const apiManager = ApiManager.getInstance();
    const response = await apiManager.post(`/api/patient-documents/${params.documentId}/share/`, {
      caregiver_id: params.caregiverId,
    });
    return response.data as ApiPatientDocumentsShareResult;
  }

  static async unshare(params: ApiPatientDocumentsUnshareParams): Promise<ApiPatientDocumentsUnshareResult> {
    const apiManager = ApiManager.getInstance();
    const response = await apiManager.post(`/api/patient-documents/${params.documentId}/unshare/`, {
      caregiver_id: params.caregiverId,
    });
    return response.data as ApiPatientDocumentsUnshareResult;
  }

  static async delete(documentId: string): Promise<void> {
    const apiManager = ApiManager.getInstance();
    await apiManager.delete(`/api/patient-documents/${documentId}/`);
  }

  static async update(params: ApiPatientDocumentsUpdateParams): Promise<ApiPatientDocumentsUpdateResult> {
    const apiManager = ApiManager.getInstance();
    const response = await apiManager.patch(`/api/patient-documents/${params.documentId}/`, {
      filename: params.filename,
    });
    return response.data as ApiPatientDocumentsUpdateResult;
  }
}
